import React, { useState } from "react";
import Card from "components/card";
import { MdInventory, MdTrendingUp, MdTrendingDown, MdWarning } from "react-icons/md";
import { Toaster } from 'react-hot-toast';

const Stock = () => {
  const [stockData] = useState({
    totalStock: 25000,
    stockByType: [
      {
        type: "Fair Trade",
        quantity: 8500,
        trend: "up",
        percentage: 15,
        status: "healthy",
        details: {
          W180: 3000,
          W210: 2500,
          W240: 3000
        }
      },
      {
        type: "Organic",
        quantity: 6500,
        trend: "down",
        percentage: 8,
        status: "warning",
        details: {
          W180: 2000,
          W210: 2500,
          W240: 2000
        }
      },
      {
        type: "Conventional",
        quantity: 10000,
        trend: "up",
        percentage: 12,
        status: "healthy",
        details: {
          W180: 4000,
          W210: 3000,
          W240: 3000
        }
      }
    ],
    recentMovements: [
      {
        id: 1,
        type: "In",
        quantity: 500,
        date: "2024-03-20",
        category: "Fair Trade",
        size: "W180",
        origin: "North Region"
      },
      {
        id: 2,
        type: "Out",
        quantity: 300,
        date: "2024-03-19",
        category: "Organic",
        size: "W210",
        destination: "Export"
      },
      {
        id: 3,
        type: "In",
        quantity: 800,
        date: "2024-03-18",
        category: "Conventional",
        size: "W240",
        origin: "South Region"
      },
      {
        id: 4,
        type: "Out",
        quantity: 450,
        date: "2024-03-17",
        category: "Fair Trade",
        size: "W180",
        destination: "Local Market"
      },
      {
        id: 5,
        type: "In",
        quantity: 600,
        date: "2024-03-16",
        category: "Organic",
        size: "W210",
        origin: "East Region"
      }
    ]
  });

  const getTrendIcon = (trend) => {
    return trend === "up" ? (
      <MdTrendingUp className="h-5 w-5 text-green-500" />
    ) : (
      <MdTrendingDown className="h-5 w-5 text-red-500" />
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "healthy":
        return "bg-green-100 text-green-800";
      case "warning":
        return "bg-yellow-100 text-yellow-800";
      case "critical":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="mt-3">
      <Toaster position="top-right" />
      
      {/* Overview Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card extra="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Stock</p>
              <h4 className="text-2xl font-bold text-navy-700 dark:text-white">
                {stockData.totalStock.toLocaleString()} kg
              </h4>
              <p className="mt-1 text-sm text-gray-500">Across all categories</p>
            </div>
            <div className="rounded-full bg-brand-50 p-3">
              <MdInventory className="h-6 w-6 text-brand-500" />
            </div>
          </div>
        </Card>

        {stockData.stockByType.map((item, index) => (
          <Card key={index} extra="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{item.type}</p>
                <h4 className="text-2xl font-bold text-navy-700 dark:text-white">
                  {item.quantity.toLocaleString()} kg
                </h4>
                <div className="mt-2 flex items-center gap-2">
                  {getTrendIcon(item.trend)}
                  <span className={`text-sm font-medium ${item.trend === "up" ? "text-green-500" : "text-red-500"}`}>
                    {item.percentage}%
                  </span>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  W180: {item.details.W180}kg | W210: {item.details.W210}kg | W240: {item.details.W240}kg
                </div>
              </div>
              <div className={`rounded-full p-3 ${getStatusColor(item.status)}`}>
                <MdWarning className="h-6 w-6" />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Recent Movements */}
      <Card extra="mt-4 p-4">
        <h4 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
          Recent Stock Movements
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="py-3 text-left text-sm font-medium text-gray-500">Type</th>
                <th className="py-3 text-left text-sm font-medium text-gray-500">Category</th>
                <th className="py-3 text-left text-sm font-medium text-gray-500">Size</th>
                <th className="py-3 text-left text-sm font-medium text-gray-500">Quantity</th>
                <th className="py-3 text-left text-sm font-medium text-gray-500">Origin/Destination</th>
                <th className="py-3 text-left text-sm font-medium text-gray-500">Date</th>
              </tr>
            </thead>
            <tbody>
              {stockData.recentMovements.map((movement) => (
                <tr key={movement.id} className="border-b border-gray-100">
                  <td className="py-3">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      movement.type === "In" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                    }`}>
                      {movement.type}
                    </span>
                  </td>
                  <td className="py-3 text-sm text-gray-600">{movement.category}</td>
                  <td className="py-3 text-sm text-gray-600">{movement.size}</td>
                  <td className="py-3 text-sm font-medium text-navy-700 dark:text-white">
                    {movement.quantity} kg
                  </td>
                  <td className="py-3 text-sm text-gray-600">
                    {movement.origin || movement.destination}
                  </td>
                  <td className="py-3 text-sm text-gray-600">
                    {new Date(movement.date).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default Stock; 